// Shapes of everything the API returns. Dates are ISO strings; days are 'YYYY-MM-DD'; times are 'HH:MM'.
import type {
  ActionKind, ActionSource, ConversationPurpose, ActionStatus, ActivityStatus, ActivityType, CalendarKind, CheckAudience, CheckStatus, DevicePlatform, FileSource,
  NotificationKind, ReminderSetBy, ReminderState, Role, SpaceColor, UpdateKind, UpdateSource, ContextSection, ContextTargetKind,
  NotificationCategory, DeliveryChannel, FileStatus, FileUnit, FollowFrequency, InsightCategory, InsightConfidence, QuoteCheck, ResearchLevel, SpeechLanguage,
} from './enums.js';
import type { ActionPayloadMap } from './schemas.js';
import type { QuickAddDraft } from './quickadd.js';

export interface ApiErrorBody {
  error: {
    code: string; message: string; /** On a 409 version conflict: the record as it is now. */ current?: unknown;
    /** On a 409 name_conflict (Knowledge base upload): the file already there and the choices. */ conflict?: NameConflict;
  };
}

/** A page of a list. Pass nextCursor back as ?cursor= to get the next page; null means the end. */
export interface Page<T> { items: T[]; nextCursor: string | null }

export interface SessionUser { id: string; email: string; name: string; initials: string; role: Role; isAdmin: boolean }
export interface User extends SessionUser { title: string | null; isActive: boolean }
export interface PersonSummary { id: string; name: string; initials: string | null }
export interface NamedRef { kind: 'user' | 'contact'; id: string; name: string }

export interface LoginResponse { user: SessionUser; expiresAt: string; /** Only for client "app". */ token?: string }
/** Badge counts. waitingActions leaves out optional cards (context suggestions); they are counted on their own. */
export interface Summary { notifications: number; waitingActions: number; contextSuggestions: number; overdueReminders: number }

/**
 * GET /seen: what other people added that you have not looked at yet (release 6.3, the blue "new" dots). Only the
 * things with something new are listed; spaces and tracks get their dot from what is in them (lib/seen.ts on the web).
 */
export interface NewSince {
  /** Activities with something new. row: the activity itself is new, or has updates, minutes or files since you opened
   *  it. updates, minutes, files: that tab has something since you last opened the tab. */
  activities: Record<string, NewOnActivity>;
  /** Tracks that are new themselves (made by someone else since you looked), with their space. */
  tracks: Record<string, { spaceId: string }>;
  /** Something new in the Knowledge base (a file or a folder) or the Research Center (an insight someone kept). */
  kb: boolean; research: boolean;
}
export interface NewOnActivity { spaceId: string; trackId: string; parentId: string | null; row: boolean; updates: boolean; minutes: boolean; files: boolean }

/** Settings, Archived: spaces (admins and the director) and tracks, each with when, by whom and what is in it. */
export interface ArchivedList {
  spaces: { id: string; name: string; clientName: string | null; archivedAt: string; archivedBy: string | null; tracks: number }[];
  tracks: { id: string; name: string; spaceId: string; spaceName: string; archivedAt: string; archivedBy: string | null; activities: number }[];
}
/** Settings, Deleted: activities deleted in the last `days` days that you can see. */
export interface DeletedList { days: number; activities: { id: string; title: string; type: string; deletedAt: string; deletedBy: string | null; where: string }[] }
export interface SpaceSummary { id: string; name: string; clientName: string | null; color: SpaceColor; tracks: number; activities: number; open: number }
/** role: in this space. userRole: the person's role in the firm (partners cannot own activities or lead tracks). */
export interface Member { id: string; name: string; initials: string; title: string | null; role: 'lead' | 'member'; userRole: Role }
export interface Contact { id: string; spaceId: string; name: string; title: string | null; organisation: string | null; email: string | null }

export interface Track {
  id: string; spaceId: string; spaceName: string; name: string; description: string | null; color: string; sort: number;
  lead: PersonSummary | null; total: number; open: number; overdue: number; atRisk: number;
  /** From the activities: at_risk when a milestone is late, blocked or at risk; watch when anything else is. */
  health: 'on_track' | 'watch' | 'at_risk'; healthWhy: string | null;
  lastMovement: string | null;
}
export interface TrackDetail extends Track {
  tags: string[];
  next: { id: string; title: string; type: ActivityType; date: string } | null;
  people: (PersonSummary & { open: number })[];
}
export interface SpaceDetail {
  id: string; name: string; clientName: string | null; description: string | null; color: SpaceColor;
  members: Member[]; contacts: Contact[]; tracks: Track[];
}

export interface Activity {
  id: string; spaceId: string; spaceName: string; trackId: string; trackName: string; trackColor: string;
  type: ActivityType; title: string; description: string | null; status: ActivityStatus;
  /** Primary owner first. */
  owners: PersonSummary[]; location: string | null;
  startsAt: string | null; endsAt: string | null; dueDate: string | null; dueTime: string | null;
  isMilestone: boolean; needsPrep: boolean; progress: number; stepsTotal: number; stepsDone: number; filesCount: number;
  requestedBy: NamedRef | null; requestedAt: string | null;
  /** Derived: requested by a client contact of the space. */
  isClientRequest: boolean;
  waitingOn: NamedRef | null; waitingSince: string | null;
  tags: string[]; parentActivityId: string | null;
  /** The meeting (or activity) this one is a follow-up of: "From". Not "Part of" (that is parentActivityId). */
  originActivityId: string | null;
  isOverdue: boolean; isDueToday: boolean;
  /** Optimistic concurrency: send it back with a PATCH. */
  version: number; updatedBy: PersonSummary | null; deletedAt: string | null; createdById: string | null;
  createdAt: string; updatedAt: string; completedAt: string | null;
}
export interface Step { id: string; position: number; title: string; done: boolean; doneAt: string | null; owner: PersonSummary | null }
export interface ActivityUpdate { id: string; body: string; kind: UpdateKind; source: UpdateSource; createdAt: string; author: PersonSummary | null }
export interface Attendee { kind: 'user' | 'contact'; id: string; name: string; initials: string | null }
export interface ActivityLink { id: string; title: string; type: ActivityType; status?: ActivityStatus }
/** A sub-activity as its parent shows it: how far along it is, and how many parts it has of its own. */
export interface SubActivity extends ActivityLink {
  status: ActivityStatus; progress: number; stepsDone: number; stepsTotal: number; dueDate: string | null; startsAt: string | null;
  owner: string | null; isOverdue: boolean; subCount: number; subDone: number;
}
/**
 * Two links (Kareem, 4 Nov). From: cameFrom is the meeting (or activity) this one is a follow-up of, and leadsTo its
 * own follow-ups. Part of: partOf is the activity this one is a part of, and subActivities its parts (any depth: each
 * can have its own), with how far along each is.
 */
export interface ActivityDetail extends Activity {
  steps: Step[]; updates: ActivityUpdate[]; attendees: Attendee[]; leadsTo: ActivityLink[]; cameFrom: ActivityLink | null;
  partOf: ActivityLink | null;
  subActivities: SubActivity[];
}

/**
 * A file is stored once and linked to folders, activities and tracks. spaceId, trackId and activityId (and the
 * titles) describe its first activity or track link, for the activity and track Files lists; alsoIn lists every place.
 */
export interface FileItem {
  id: string; name: string; size: number; mime: string; source: FileSource;
  spaceId: string | null; trackId: string | null; activityId: string | null;
  activityTitle: string | null; trackName: string | null; addedBy: PersonSummary | null; createdAt: string; updatedAt: string;
  /** No link yet: an assistant paperclip upload waiting for its card. */
  staged: boolean;
  status: FileStatus; unreadableReason: string | null;
  /** Pages, sheets or slides (see unit). Null until read. */
  pageCount: number | null; unit: FileUnit | null; language: string | null; ocr: boolean;
  version: number;
  alsoIn: FileLink[];
  /** One line from the summary ("What it is"), when there is one. */
  summaryLine: string | null;
  /** Who added the current version (the "Added by" in a folder's list; addedBy is who added the file first). */
  versionBy: PersonSummary | null;
}
export interface FileLink { kind: 'folder' | 'activity' | 'track'; id: string; name: string; spaceId: string | null }

/** A page (or sheet, or slide) reference in a summary. ref is its number; label is the sheet name for Excel. */
export interface FileKeyPoint { text: string; ref: number | null }
export interface FileSummary {
  whatItIs: string;
  keyPoints: FileKeyPoint[];
  dates: { date: string; what: string }[];
  peopleOrgs: { name: string; role: string }[];
  numbers: { value: string; what: string }[];
  /** "42 pages", "3 sheets". */
  length: string;
  language: string;
}
export interface FileSummaryView {
  summary: FileSummary;
  /** ai: "Written by the assistant". user: "Edited by Sara, 1 Nov". */
  author: 'ai' | 'user'; editedBy: PersonSummary | null; editedAt: string | null; generatedAt: string;
  /** The version the summary was written for. */
  version: number;
  /** "What changed from v2", on a summary of a version after the first. */
  whatChanged: string | null; comparedTo: number | null;
}
export interface FileVersion {
  version: number; name: string; size: number; mime: string; addedBy: PersonSummary | null; addedAt: string; whatChanged: string | null; current: boolean;
}
export interface FileDetail extends FileItem { summary: FileSummaryView | null; versions: FileVersion[] }

export interface Folder {
  id: string; name: string; description: string;
  /** null: Team-wide. */
  spaceId: string | null; spaceName: string | null;
  createdBy: PersonSummary | null; createdAt: string; updatedAt: string;
  fileCount: number;
  /** The creator, the space lead and the director can edit, rename, move or delete it. */
  canEdit: boolean;
  /** Anyone who can see it can add files, except partners (read only). */
  canAdd: boolean;
  /** "Updated 27 Oct by Faisal": the latest file added or changed in it (else the folder itself). */
  lastChange: { at: string; by: string | null };
}
export interface FolderDetail extends Folder { files: FileItem[] }

/** A presigned upload: POST the fields and the file to url (S3), or PUT the bytes to url (local storage). */
export interface UploadTicket {
  uploadId: string; method: 'POST' | 'PUT'; url: string; fields: Record<string, string>;
  /** The name the file will have ("Report (2).pdf" when keeping both). */
  name: string;
  /** Set when this upload adds a version to an existing file. */
  fileId: string | null; version: number | null;
  maxBytes: number; expiresAt: string;
}
/** 409 name_conflict body extra: a file with this name is already in the folder. */
export interface NameConflict { existing: FileItem; nextVersion: number; options: ('version' | 'keep_both')[] }

export interface SearchHit {
  kind: 'file'; file: FileItem;
  /** Where it matched: the name, the summary, or the text (with the page, sheet or slide). */
  matched: 'name' | 'summary' | 'text';
  snippet: string | null; ref: number | null; refLabel: string | null;
}

/** The Search page: one kind of thing found. */
export const SEARCH_KINDS = ['activity', 'track', 'minutes', 'email', 'file', 'person', 'insight'] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];
export interface SearchResult {
  kind: SearchKind; id: string; title: string;
  /** Where it is ("AI Office · Governance and Policy", "Knowledge base · Dallah policies · found in the full text, p. 21"). */
  where: string;
  /** Its tags, without the # (an activity's own; minutes and emails carry their activity's; a track the tags in it). */
  tags: string[];
  /** The date it is about (a meeting's start, a due date, when it was logged or changed); null for people. */
  when: string | null;
  /** The page that opens it. */
  href: string;
  /** For files: the words around the match. */
  snippet: string | null;
  /** For activities: meeting, workshop, deliverable or task. */
  activityType: string | null;
  /** Higher first: the words are in the title (2), or only elsewhere (1). */
  score: number;
}
export interface SearchResults {
  q: string; items: SearchResult[];
  /** Per kind, after the tags and before the kind is chosen: what each switch would show. */
  counts: Record<SearchKind, number>;
  /** The tags on what was found, most used first (and any chosen tag, even with none left). */
  tags: { tag: string; n: number }[];
}
/** "Ask the assistant" from Search: the conversation it started, and the answer. */
export interface SearchAnswer { conversationId: string; answer: string; sources: { label: string; link: string | null }[] }

export interface Reminder {
  id: string; text: string; remindAt: string | null; remindDate: string | null; dueAt: string;
  forUser: { id: string; name: string };
  /** True when someone else set it for the person it is for ("Set by Kareem for you"). */
  setByOther: boolean;
  activity: { id: string; title: string; dueDate: string | null; spaceName: string | null } | null;
  setBy: ReminderSetBy; setByUser: { id: string; name: string } | null;
  doneAt: string | null; notifiedAt: string | null; state: ReminderState; version: number;
}

export interface AppNotification {
  id: string; kind: NotificationKind; title: string; body: string | null; payload: { context?: string | null; [k: string]: unknown };
  link: string | null; isAi: boolean; actor: PersonSummary | null; createdAt: string; dismissedAt: string | null;
  activityId: string | null; reminderId: string | null; teamCheckId: string | null;
  /** Present when the row carries an AI action; its state comes from the one proposed actions list. */
  action: { id: string; kind: ActionKind; status: ActionStatus; version: number } | null;
  /** Which category of "You hear about" it belongs to. */
  category: NotificationCategory;
  /** How it reached the person: always the app; push or email only when one of those really delivered it. */
  via: DeliveryChannel[];
  /** What went wrong, in the service's own words (an error code, the API's reply), behind "Details". Only admins get
   *  it; everyone else gets null. The title and body are always plain words. */
  detail: string | null;
}

/** Per person settings kept on the server, so they follow the person to every device. */
export interface UserPrefs {
  notifications: {
    channels: Record<NotificationCategory, { push: boolean; email: boolean }>;
    /** Push and email wait until the end of quiet hours (team time zone). null: no quiet hours. */
    quietHours: { from: string; to: string } | null;
  };
  /** Requests: the context suggestion groups (by space id) this person left open. Collapsed by default. */
  openGroups: string[];
  /** The language this person speaks to the app in (Speak, Talk, Record). English by default. */
  voice: { language: SpeechLanguage };
  /** The morning email (07:00 Riyadh, Sunday to Thursday). On by default. */
  morningEmail: boolean;
}
export interface NotificationList { items: AppNotification[]; open: number; /** Pages through dismissed history. */ nextCursor: string | null }

export interface DetailRow { label: string; value: string }
export type ProposedAction = {
  [K in ActionKind]: {
    id: string; kind: K; kindLabel: string; source: ActionSource; sourceLabel: string; title: string;
    payload: ActionPayloadMap[K]; status: ActionStatus; version: number; updated: boolean;
    conversationId: string | null; spaceId: string | null; spaceName: string | null;
    /** The person the card is for (the director may see and decide anyone's). */
    owner: PersonSummary;
    origin: Record<string, unknown>; result: Record<string, unknown> | null; rows: DetailRow[];
    createdAt: string; updatedAt: string; decidedAt: string | null;
  }
}[ActionKind];

export interface CalendarItem {
  id: string; kind: CalendarKind; title: string; date: string; start: string | null; end: string | null; allDay: boolean;
  isMilestone: boolean; isFollowUp: boolean; waitingOn: string | null; aiSet: boolean; status: ActivityStatus | null; done: boolean;
  spaceId: string | null; spaceName: string | null; trackId: string | null; trackName: string | null; owner: string | null; activityId: string | null;
  /** The first owner's initials (the Week card's avatar); prep: needs prep, and its steps done of total. Reminders: none. */
  ownerInitials: string | null; needsPrep: boolean; stepsDone: number; stepsTotal: number;
}

export interface Conversation {
  id: string; title: string; spaceId: string | null; spaceName: string | null;
  /** Narrower than the space: the track or activity the panel was opened on (the assistant reads its context). */
  trackId: string | null; trackName: string | null; activityId: string | null; activityTitle: string | null;
  /** A Knowledge base folder or file the panel was opened on (the assistant reads their text). */
  folderId: string | null; folderName: string | null; fileId: string | null; fileName: string | null;
  purpose: ConversationPurpose | null;
  createdAt: string; updatedAt: string;
}
/** One line of an answer's facts table; warn shows it in red (late, stale, at risk). */
export interface AnswerFact { label: string; value: string; warn?: boolean }
/** A numbered source under an answer. link only when it points at something the person can open. */
export interface AnswerSource { label: string; link?: string | null }
export interface MessageMeta {
  actionIds?: string[]; approvedIds?: string[]; ai?: string;
  /** A structured answer: a facts table, numbered sources, and suggested follow-up questions. */
  facts?: AnswerFact[]; sources?: AnswerSource[]; followUps?: string[];
  /** Options to pick from when the reply asks a question; pressing one sends it. The first is the suggested one. */
  choices?: string[];
  /** The Knowledge base was used: "Checked 8 summaries, read 2 files". */
  knowledge?: { summariesChecked: number; filesRead: number; line: string };
  /** A page the assistant opened (navigation needs no approval): shown as a link, and followed where the person is. */
  nav?: { to: string; label: string };
  /** Voice control: what it did on the screen (scroll, start dictation, go back). */
  ui?: ({ type: 'scroll'; to: 'up' | 'down' | 'top' | 'bottom' } | { type: 'dictate' } | { type: 'back' })[];
  /** The cards this message approved or ignored by name ("approve the reminder"). */
  decidedIds?: string[];
  /** Voice control: nothing to say out loud (it acted on the screen, or a card is shown). */
  silent?: boolean;
}
/** "Try saying" on a new conversation, made from the person's own work. */
export interface Starter { text: string; hint: string; /** false: it ends mid-sentence, so it goes into the box for you to finish instead of being sent. */ complete: boolean }
export interface Message { id: string; role: 'user' | 'assistant'; content: string; meta: MessageMeta; createdAt: string }
export interface ConversationDetail extends Conversation { messages: Message[]; actions: ProposedAction[]; stagedFiles: FileItem[] }
export interface MessageResult {
  conversation: Conversation; userMessage: Message; reply: Message;
  /** New or updated cards from this turn. */
  actions: ProposedAction[]; approved: ProposedAction[]; errors: string[];
}

export interface TeamCheckAnswer { id: string; user: PersonSummary; answer: string; createdAt: string; updatedAt: string }
/** What staff (the asker when not a partner, the recipients, the director) see. */
export interface TeamCheck {
  view: 'full';
  id: string; question: string; askedBy: PersonSummary; audience: CheckAudience; status: CheckStatus;
  requiresReview: boolean; reviewer: PersonSummary | null; hasConflict: boolean; conversationId: string | null;
  /** The assistant's guess at whose question this is, for the director. */
  suggestedOwner: PersonSummary | null; suggestionNote: string | null;
  about: { kind: 'activity' | 'track' | 'space'; id: string; title: string } | null;
  summary: string | null; confirmedAnswer: string | null; confirmedBy: PersonSummary | null; confirmedAt: string | null;
  createdAt: string; expiresAt: string; closedAt: string | null;
  recipients: (PersonSummary & { answered: boolean; followUp: string | null; addedBy: string | null })[];
  answers: TeamCheckAnswer[];
}
/** What a partner sees of their own question: no people, no routing, no status trail. */
export interface RequesterCheck {
  view: 'requester';
  id: string; question: string; about: { kind: 'activity' | 'track' | 'space'; id: string; title: string } | null;
  state: 'waiting' | 'answered'; answer: string | null; answeredAt: string | null; conversationId: string | null; createdAt: string;
}
export type CheckView = TeamCheck | RequesterCheck;

export interface SessionInfo {
  id: string; client: 'web' | 'app'; userAgent: string | null; ip: string | null;
  createdAt: string; lastSeenAt: string; expiresAt: string; current: boolean;
}
/** A person just added: when no password was set, the link they were sent (or to share by hand). */
export type CreatedUser = User & { invite: ResetLink | null };
export interface ResetLink {
  url: string; expiresAt: string;
  /** The address the link was emailed to; null when email is not connected (copy and share it by hand). */
  emailedTo: string | null;
}
/** One line of a meeting transcript: the second from the start, who spoke, what was said. */
export interface TranscriptLine { at: number; who: string; t: string }
/** What the assistant noted while recording, each with the second: [second, text]. */
export interface LiveNotes { topics: [number, string][]; decisions: [number, string][]; actions: [number, string][]; meetings: [number, string][] }
export interface MeetingMinutes {
  summary: string;
  topics: { at: number; heading: string; detail: string }[];
  decisions: string[]; next: string[];
  actions: { title: string; at: number | null; ownerIds: string[]; dueDate: string | null; type: 'task' | 'deliverable'; trackId: string | null; speaker?: string | null }[];
  setup: { title: string; detail: string; at: number | null; trackId: string | null }[];
}
export type RecordingStatus = 'recording' | 'processing' | 'ready' | 'failed';
/**
 * A voice in a recording. label: what the voice service calls it ("Speaker 1"). suggested: the assistant's guess
 * (violet until confirmed). name: the confirmed name (a person picked or typed it).
 */
export interface Speaker { label: string; name: string | null; suggested: string | null; confirmed: boolean }
/** A recording, as Record and Meetings show it. */
export type RecordingStage = 'transcribing' | 'drafting' | 'matching';
export interface Recording {
  id: string; activityId: string | null; title: string; trackId: string; status: RecordingStatus;
  startedAt: string; endedAt: string | null; durationSecs: number | null; recordedBy: PersonSummary;
  marks: number[]; lines: TranscriptLine[]; notes: LiveNotes; minutes: MeetingMinutes | null;
  /** The voices the service separated, with suggested or confirmed names; people: who they could be (to pick from). */
  speakers: Speaker[]; people: string[];
  /** The card with the minutes, once written. */
  actionId: string | null; actionStatus: 'waiting' | 'approved' | 'rejected' | null;
  hasAudio: boolean; partsWaiting: number; error: string | null;
  /**
   * Where writing the minutes has got to, while it is processing: transcribing (the parts left and the whole
   * recording), drafting (the minutes, decisions and actions), matching (to the track and owners, making the card).
   */
  stage: RecordingStage | null;
  /** How long each part is (seconds): the browser sends one whole audio file per part while recording. */
  partSeconds: number;
}
/** Minutes: waiting for review (a card), filed (approved, or typed by hand), not recorded (past, none), upcoming. */
export type MinutesState = 'wait' | 'ok' | 'none' | 'up' | 'processing';
export interface MeetingRow {
  id: string; title: string; type: 'meeting' | 'workshop'; startsAt: string | null; endsAt: string | null;
  spaceId: string; spaceName: string; trackId: string; trackName: string; trackColor: string;
  with: string[]; location: string | null; owner: PersonSummary | null;
  past: boolean; today: boolean;
  recordingSecs: number | null; minutes: MinutesState; actions: number | null;
}
export interface MeetingDetail extends MeetingRow {
  recording: Recording | null;
  /** The minutes as filed on the activity (approved, or typed by hand), newest first. */
  filed: { id: string; body: string; createdAt: string; author: PersonSummary | null; source: UpdateSource }[];
  /** What the approved card created (actions as activities). */
  actionsFiled: { id: string; title: string; type: ActivityType; owner: PersonSummary | null; dueDate: string | null }[];
  /** Upcoming: the agenda (description lines), prep (steps), and the last meeting on this track with minutes. */
  agenda: string[]; prep: { title: string; done: boolean }[];
  last: { id: string; title: string; startsAt: string | null; summary: string } | null;
  canRecord: boolean;
}

export type UsageRange = 'week' | '4weeks' | 'quarter';
/** Settings, Usage (admins only): time in the app by person and day, when the team is in it, and the month's costs. */
export interface UsageReport {
  range: UsageRange; from: string; to: string;
  people: { id: string; name: string; initials: string; role: string; isAdmin: boolean; hours: number[]; total: number; lastSeen: string | null }[];
  /** For the person chosen (or everyone): minutes in each working day and hour (Sun to Thu by 07:00 to 22:00). */
  heat: number[][];
  totals: { hours: number; avgPerDay: number; daysIn: number; daysOf: number; peakHour: string | null };
  /** This month so far, estimated in US dollars. */
  costs: { month: string; anthropic: number; elevenlabs: number; aws: number; awsHosting: number };
  /** Research this month by level (DECISIONS, Research levels): tasks run, web searches, estimated US dollars (searches and tokens). */
  research: { level: 'quick' | 'standard' | 'deep' | 'follow' | 'manual'; tasks: number; searches: number; cost: number }[];
  /** The whole team in the range, whoever is chosen (6.3): the four numbers at the top. */
  stats: { activeToday: number; people: number; avgPerDayMin: number; logged: number; preps: UsagePreps; workDays: number };
  /** Each person in the range (6.3, the Team table), everyone even when one person is chosen. */
  team: UsagePerson[];
  /** Usage and delivery by week (6.3): team active hours and preps ready on time, oldest first; trend is plain words. */
  weeks: { from: string; label: string; hours: number; preps: UsagePreps }[];
  trend: string;
}
/** Prep steps (or meetings) that were due in a range, and how many were ready before it started. */
export interface UsagePreps { onTime: number; of: number }
export interface UsagePerson {
  id: string; name: string; initials: string;
  /** Average active minutes per working day so far in the range, and working days with any time in the app. */
  perDayMin: number; daysActive: number; daysOf: number;
  /** The hours they are most in the app ("09 to 11, 21 to 23"), from their own minutes. */
  peak: string | null;
  /** New activities and updates they added. */
  logged: number;
  preps: UsagePreps;
  /** Requests spoken in voice mode, and minutes of meetings they recorded. */
  voice: { asks: number; recordedMin: number };
}

/** Today's four cards (desktop, 6.3): this working week, Sunday to Thursday (Friday and Saturday look at the next one). */
export interface TodayCards {
  week: { from: string; to: string };
  /** Deliverables and tasks due in the week, not done, in the spaces you see; yours: you own them. */
  due: { open: number; yours: number };
  /** Your meetings and workshops in the week (as Today counts them) and the next one from now. */
  meetings: { total: number; next: { id: string; title: string; startsAt: string } | null };
  /** The assistant's cards waiting for you (context suggestions aside), by kind ("1 email"). */
  approvals: { total: number; mix: { word: string; n: number }[] };
  /** Research Center: insights kept this week; new ones waiting to be reviewed (null for partners, who see only kept ones). */
  knowledge: { kept: number; toReview: number | null };
}

/** Morning email (6.3): your switch, whether this server can send it, and (admins) who has it on. */
export interface MorningEmailStatus {
  on: boolean; sending: boolean; replies: boolean;
  people: { id: string; name: string; on: boolean; lastSent: string | null }[];
}

/** A saved filter on the Search page: personal (only its owner sees it), with how many results it has now. */
export interface SavedSearch { id: string; name: string; q: string; kind: SearchKind | null; tags: string[]; count: number; href: string }
/** Email on this server: whether messages are sent, whether the inbound address is read, and which address it is. */
export interface EmailStatus {
  sending: boolean; receiving: boolean;
  /** Where people forward or copy email to log it (INBOUND_ADDRESS). */
  inboundAddress: string;
  /** Admins only (empty for everyone else): what failed for good in the last 14 days, newest first. */
  problems: EmailProblem[];
  /**
   * Emails the assistant could not place, still waiting for a person to log or dismiss them: your own; the director
   * sees everyone's. Newest first.
   */
  unplaced: UnplacedEmail[];
}
/** An email to the inbound address with no place yet: its card (Log it, Dismiss) and why the assistant left it. */
export interface UnplacedEmail { actionId: string; subject: string; from: string | null; reason: string; at: string; owner: { id: string; name: string } }
/** An email that was not sent (out), or one that reached the inbound address but could not be read (in). */
export interface EmailProblem { direction: 'out' | 'in'; who: string; subject: string; error: string; at: string }
export interface Health {
  ok: boolean; worker: { lastBeatAt: string | null; stale: boolean };
  /** What the API and the database think the time is (ISO). */
  time: { server: string; database: string };
}

export interface Device { id: string; platform: DevicePlatform }

// ---------- Space and Track context ----------
export interface ContextSectionView {
  key: ContextSection; label: string; text: string; updatedAt: string | null; updatedBy: PersonSummary | null;
}
export interface ContextDoc {
  target: { kind: ContextTargetKind; id: string; name: string; spaceId: string; spaceName: string };
  /** Send it back when saving; a newer version on the server gives a 409 with the current doc. */
  version: number;
  sections: ContextSectionView[];
  chars: number; maxChars: number;
  updatedAt: string | null; updatedBy: PersonSummary | null;
  /** May edit directly (director, admin, the space lead, or this track's lead). */
  canEdit: boolean;
  /** May ask the assistant to change it (the card goes to someone who can edit). */
  canPropose: boolean;
}
export interface ContextHistoryEntry {
  id: string; version: number; section: ContextSection; sectionLabel: string; before: string; after: string;
  changedBy: PersonSummary | null; changedAt: string; via: 'manual' | 'assistant';
}

// ---------- reminders from words ----------
/** What the assistant understood from a spoken or typed reminder. Only what was said is filled in. */
export interface ReminderDraft {
  text: string | null; date: string | null; time: string | null;
  forUserId: string | null; activityId: string | null;
  /** Which fields were filled from the words (the form tints them until edited). */
  filled: ('text' | 'date' | 'time' | 'forUserId' | 'activityId')[];
  /** Things the server dropped or could not use, in plain words. */
  notes: string[];
}
export interface Transcript {
  /** The words heard, with the voice service's tags for sounds ("[pausa]", "(laughs)") taken out. Never empty: a
   *  recording with no real words is a 422 no_speech. */
  text: string;
  /** true when no voice service is connected and a sample sentence was returned. */
  demo: boolean;
}
export interface Teammate { id: string; name: string; initials: string; role: Role }

/** The assistant's short brief on a space (Stands, Risk, Next), a track (a paragraph) or an activity (what it knows). */
/** day: the person's own "Your day" (its target is the person). */
export type BriefKind = 'space' | 'track' | 'activity' | 'day';
export interface BriefLine { label: string; text: string; tone?: 'red' | 'amb' | 'grn' | null }
export interface Brief {
  kind: BriefKind; targetId: string; lines: BriefLine[]; paragraph: string | null;
  /** What it was written from, in plain words ("4 updates, the AI Office context"). */
  basis: string; generatedAt: string;
  /** Something changed since it was written; a fresh one is on its way. */
  stale: boolean;
}

/** The right-hand column of a track: key dates, signals, meeting chains (people are on TrackDetail). */
export interface TrackOverview {
  keyDates: { id: string; title: string; date: string; done: boolean }[];
  signals: { tone: 'red' | 'amb' | 'ai'; title: string; detail: string; activityId: string | null }[];
  chains: { id: string; title: string; date: string | null; followUps: number; done: number; prepOpen: number }[];
}
/** One entry in a track's Thread, Minutes or Emails tab: an update on one of its activities. */
export interface TrackUpdate {
  id: string; activity: { id: string; title: string }; author: PersonSummary | null; kind: UpdateKind; source: UpdateSource; body: string; createdAt: string;
}

/** How a due item stands, in a word for the label ("Scoring matrix v2 · On track"). bad: behind, late or not started. */
export interface DueState { label: 'Done' | 'Overdue' | 'Behind' | 'Not started' | 'On track'; bad: boolean }
/** Something with a date on Today: a meeting or workshop at its time, a deliverable or task on its due date. */
export interface TodayItem {
  id: string; type: ActivityType; title: string; status: ActivityStatus; isMilestone: boolean; isClientRequest: boolean;
  /** Local date (YYYY-MM-DD); start and end are local HH:MM; for a due item start is its due time (or null). */
  date: string; start: string | null; end: string | null;
  needsPrep: boolean; stepsDone: number; stepsTotal: number; progress: number; state: DueState;
  owner: string | null; attendees: string[]; requestedBy: string | null; waitingOn: string | null;
  spaceId: string; spaceName: string; trackId: string; trackName: string;
}
/** Yours: one of your activities at risk, blocked or not started and due soon, or a prep step of yours for a meeting soon. */
export interface TodayYours {
  kind: 'risk' | 'prep'; activityId: string; stepId: string | null; title: string; why: string;
  /** For Nudge and "I will call": the first other owner of the activity, if any. */
  other: { id: string; name: string } | null;
  spaceId: string; spaceName: string; trackId: string; trackName: string;
}
/** Gone quiet: an open activity with no update for a week, or a track with no movement. */
export interface TodayQuiet {
  kind: 'activity' | 'track'; id: string; title: string; why: string; days: number; tone: 'red' | 'amb';
  owner: { id: string; name: string } | null; spaceId: string; trackId: string;
}
export interface TodayData {
  date: string; tomorrow: string;
  /** Today's meetings and workshops, and the due items for today and tomorrow (yours, or in what you lead). */
  meetings: TodayItem[]; due: { today: TodayItem[]; tomorrow: TodayItem[] };
  /** Seven days from today, each with every dated item on it. */
  week: { date: string; weekend: boolean; items: TodayItem[] }[];
  yours: TodayYours[]; quiet: TodayQuiet[];
}
/** Ask this folder: the answer, its sources (file and page; open the file sheet at that key point), and what was read. */
export interface FolderAnswer {
  question: string; answer: string;
  cites: { label: string; fileId: string; ref: number | null }[];
  /** "Checked 8 summaries, read 2 files". */
  line: string;
}

// ---------- Research Center ----------

export interface InsightEvidence {
  claim: string; quote: string; url: string; domain: string; published: string | null;
  /** Checked by code against the text the research read. */
  check: QuoteCheck; checkLabel: string;
}
export interface Insight {
  id: string; title: string; category: InsightCategory; summary: string; keyPoints: string[]; details: string; why: string;
  sourceDomain: string | null; sourceUrl: string | null; published: string | null; evidence: InsightEvidence[];
  /** Where it came from, as people say it ("Followed link: regulator.example/news", "Research task: ...", "Added by Kareem"). */
  originLabel: string; origin: 'follow' | 'task' | 'run' | 'manual'; followId: string | null; taskId: string | null; runId: string | null;
  /** new: unverified, found by the assistant (quarantined). kept: verified by a person. dismissed. */
  status: 'new' | 'kept' | 'dismissed';
  /**
   * "Verified by Sara · today" (kept: who kept it and when, decidedBy and decidedAt; one added by hand is verified by
   * whoever added it), "Dismissed by Omar", "Unverified" (found by the assistant, not kept yet).
   */
  byLabel: string; decidedBy: PersonSummary | null; decidedAt: string | null; keptAnyway: boolean;
  /** Some quote was not found, or a number in a claim is not in its quote. */
  checkFailed: boolean;
  /** Set by hand (confidenceSet), else worked out: contested while an open contradiction holds it, else from the sources. */
  confidence: InsightConfidence; confidenceSet: boolean;
  /** Open contradictions it is in (the sheet only; [] in lists). A partner gets no title for an unverified other side. */
  contradictions: { id: string; otherId: string; otherTitle: string | null; note: string; originLabel: string }[];
  /** Short words without the # ("validation"); the assistant suggests them, a person can change them. */
  tags: string[];
  tracks: { id: string; name: string; spaceName: string }[];
  related: { id: string; title: string; category: InsightCategory }[];
  savedTo: { folderId: string; folderName: string; fileId: string }[];
  createdAt: string;
}
/** One side of a contested pair. hidden: an unverified finding, for a partner (only that it exists). */
export interface ContestedSide {
  id: string; title: string | null; category: InsightCategory; status: Insight['status']; byLabel: string; summary: string | null;
  claim: string | null; quote: string | null; sourceDomain: string | null; sourceUrl: string | null; hidden: boolean;
}
/** Two insights whose sources disagree, open until a person resolves it. */
export interface ContestedPair {
  id: string; origin: 'person' | 'assistant'; /** "Marked by Sara, today", "Flagged by the assistant, today". */ originLabel: string;
  note: string; createdAt: string; sides: [ContestedSide, ContestedSide];
}
export interface FollowCheck { at: string; summary: string; changed: boolean; insights: { id: string; title: string }[] }
export interface Follow {
  id: string; name: string; url: string; domain: string; watchFor: string; frequency: FollowFrequency; frequencyLabel: string;
  lastCheckedAt: string | null; nextCheckAt: string; lastResult: string | null; foundThisMonth: number;
  createdBy: PersonSummary | null; checks: FollowCheck[];
}
export interface ResearchReport {
  title: string; summary: string;
  findings: { text: string; support: string; sources: number }[];
  disagreements: string; meaning: string; sources: { domain: string; url: string }[];
}
export interface ResearchTask {
  id: string; question: string; level: ResearchLevel; levelLabel: string;
  status: 'queued' | 'running' | 'needs_input' | 'done' | 'failed'; statusLabel: string;
  originLabel: string; askedBy: PersonSummary | null; inputQuestion: string | null;
  /** What it produced, in a few words ("Report, 4 insights", "Short answer, 5 links", "About 6 min left"). */
  outLabel: string;
  answer: string | null; links: { domain: string; url: string; note: string }[]; report: ResearchReport | null;
  insights: { id: string; title: string; category: InsightCategory; status: Insight['status'] }[];
  sources: number; claimsChecked: number; searches: number;
  error: string | null; startedAt: string | null; finishedAt: string | null; createdAt: string;
}
export interface ResearchRun {
  id: string; title: string; summary: string | null; candidates: number; kept: number; status: 'running' | 'done' | 'failed';
  topics: string[]; insights: { id: string; title: string }[]; startedAt: string; finishedAt: string | null;
}
export interface ResearchTopic { id: string; text: string; lastRunAt: string | null; createdBy: PersonSummary | null }
export interface ResearchOverview {
  /**
   * newInsights: found by the assistant, waiting to be verified (0 for a partner). verified: kept insights;
   * verifiedThisWeek: kept in the last 7 days. following: links followed.
   */
  counts: { newInsights: number; following: number; openTasks: number; verified: number; verifiedThisWeek: number };
  /** What the new insights mean together (today), or null when there is nothing new. */
  reading: { text: string; insightIds: string[]; generatedAt: string } | null;
  week: { text: string; from: string; to: string } | null;
  nextRunAt: string; topics: ResearchTopic[];
  /** Admins and the Research lead edit topics. */
  canEditTopics: boolean; lead: PersonSummary | null;
}

// ---------- natural-language quick add ----------
/** POST /spaces/:id/quick-add/parse: the model's reading of a quick-add line. Ids only from the space. */
export interface QuickParseResult {
  draft: QuickAddDraft;
  /** Ids the model gave that are not in this space, dropped (and said in plain words). */
  notes: string[];
  ai: string;
}

// ---------- voice control ----------
export type VoiceTargetKind = 'space' | 'track' | 'activity' | 'meeting' | 'folder' | 'file' | 'person';
/** One thing voice control can open by name: what the person can see, with where it opens. */
export interface VoiceTarget { kind: VoiceTargetKind; id: string; name: string; path: string; where: string | null; /** The space it is in (none for a file, a teammate or a Team-wide folder). */ spaceId: string | null }
/** GET /voice/index: every name voice control matches against (loaded once a session, refreshed on change). */
export interface VoiceIndex { targets: VoiceTarget[]; generatedAt: string }
